import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Switch, Label } from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { useCookieConsentStore, POSTHOG_COOKIE_KEY } from "@/zapi-frontend/shared/hooks/useCookieConsent";
import { enablePostHog, disablePostHog } from "@/zapi-frontend/app/providers/analytics/model/services";

const REQUIRED_COOKIES = [
  { name: "sessionId", description: "User authentication" },
  { name: "_cfuvid", description: "Cloudflare bot protection" }
];

const ANALYTICS_COOKIES = [
  { key: POSTHOG_COOKIE_KEY, description: "PostHog session and analytics cookies" }
];

export function CookieConsentModal() {
  const {
    showConsentModal,
    showConfigureView,
    cookies: storedCookies,
    closeModal,
    savePreferences
  } = useCookieConsentStore();

  const [showConfigure, setShowConfigure] = useState(false);
  const [cookies, setCookies] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (showConsentModal) {
      setShowConfigure(showConfigureView);
      setCookies(storedCookies);
    }
  }, [showConsentModal, showConfigureView, storedCookies]);

  const allEnabled = Object.fromEntries(
    ANALYTICS_COOKIES.map((c) => [c.key, true])
  );

  const handleAcceptAll = () => {
    savePreferences({ ...cookies, ...allEnabled });
    enablePostHog();
    closeModal();
  };

  const handleSavePreferences = () => {
    savePreferences(cookies);
    if (cookies[POSTHOG_COOKIE_KEY]) {
      enablePostHog();
    } else {
      disablePostHog();
    }
    closeModal();
  };

  if (!showConsentModal) return null;

  if (showConfigure) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-[400px] rounded-lg border bg-background p-4 shadow-lg">
        <div className="flex items-start justify-between mb-3">
          <FigmaTypography variant="heading-2" color="zinc900">
            Cookie Preferences
          </FigmaTypography>
          <AppButton
            appearance="text"
            size="icon"
            className="-mt-1 -mr-1"
            onClick={handleSavePreferences}
            startAdornment={<X className="h-4 w-4" />}
          />
        </div>

        <FigmaTypography variant="body-3" color="zinc500" className="mb-4">
          We use cookies to enhance your experience. Required cookies are necessary for the application to function.
          Analytics cookies help us improve our service.
        </FigmaTypography>

        <div className="space-y-4 mb-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <FigmaTypography variant="label-1" color="zinc900">
                Required Cookies
              </FigmaTypography>
              <Switch checked disabled />
            </div>
            <div className="space-y-1 pl-1">
              {REQUIRED_COOKIES.map((cookie) => (
                <div key={cookie.name} className="flex flex-col">
                  <FigmaTypography variant="label-2" color="zinc700">
                    {cookie.name}
                  </FigmaTypography>
                  <FigmaTypography variant="body-3" color="zinc500">
                    {cookie.description}
                  </FigmaTypography>
                </div>
              ))}
            </div>
          </div>

          {ANALYTICS_COOKIES.map((cookie) => (
            <div key={cookie.key} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor={cookie.key} className="cursor-pointer">
                  <FigmaTypography variant="label-1" color="zinc900">
                    {cookie.description}
                  </FigmaTypography>
                </Label>
                <Switch
                  id={cookie.key}
                  checked={cookies[cookie.key] ?? true}
                  onCheckedChange={(enabled) => setCookies((prev) => ({ ...prev, [cookie.key]: enabled }))}
                />
              </div>
              <div className="pl-1">
                <FigmaTypography variant="label-2" color="zinc700">
                  {cookie.key}
                </FigmaTypography>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 justify-end">
          <AppButton appearance="outlined" color="zinc950" onClick={handleSavePreferences}>
            <FigmaTypography variant="label-2" color="zinc900">
              Save Preferences
            </FigmaTypography>
          </AppButton>
          <AppButton appearance="contained" color="zinc950" onClick={handleAcceptAll}>
            <FigmaTypography variant="label-2" color="zinc100">
              Accept All
            </FigmaTypography>
          </AppButton>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[400px] rounded-lg border bg-background p-4 shadow-lg">
      <div className="flex items-start justify-between mb-3">
        <FigmaTypography variant="heading-2" color="zinc900">
          Cookie Notice
        </FigmaTypography>
        <AppButton
          appearance="text"
          size="icon"
          className="-mt-1 -mr-1"
          onClick={handleAcceptAll}
          startAdornment={<X className="h-4 w-4" />}
        />
      </div>

      <FigmaTypography variant="body-2" color="zinc500" className="mb-4">
        We use cookies to enhance your experience. You can accept all cookies or configure your preferences.
      </FigmaTypography>

      <div className="flex gap-2 justify-end">
        <AppButton appearance="outlined" color="zinc950" onClick={() => setShowConfigure(true)}>
          <FigmaTypography variant="label-2" color="zinc900">
            Configure
          </FigmaTypography>
        </AppButton>
        <AppButton appearance="contained" color="zinc950" onClick={handleAcceptAll}>
          <FigmaTypography variant="label-2" color="zinc100">
            Accept All
          </FigmaTypography>
        </AppButton>
      </div>
    </div>
  );
}
